import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Slide, Paper, Stack, Typography, IconButton, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dismissToast } from "../../Store/Slices/toastSlice";
import { deleteNotificationIcon, successNotificationIcon, warningNotificationIcon } from "../../AssetsV2";
import AppIcon from "./AppIcon";


const getVariantMeta = (variant) => {
    switch (variant) {
        case "success":
            return { Icon: successNotificationIcon, badgeBg: "#1B5E20" };
        case "warning":
            return { Icon: warningNotificationIcon, badgeBg: "#7A4F00" };
        case "error":
            return { Icon: deleteNotificationIcon, badgeBg: "#7F1D1D" };
        default:
            return { Icon: successNotificationIcon, badgeBg: "#0B4F6C" };
    }
};

/**
 * Renders a single toast. Manages its own auto-hide timer and exit animation.
 * `dismissToast` is dispatched only after the Slide exit animation completes,
 * so Redux state (and queue promotion) updates at the right moment.
 */
function SingleToast({ toast }) {
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(true);
    const timerRef = useRef(null);

    const startDismiss = useCallback(() => {
        clearTimeout(timerRef.current);
        setVisible(false);
    }, []);

    // Independent auto-hide timer per toast
    useEffect(() => {
        timerRef.current = setTimeout(startDismiss, toast.durationMs);
        return () => clearTimeout(timerRef.current);
    }, [toast.id, toast.durationMs, startDismiss]);

    const handleExited = () => dispatch(dismissToast(toast.id));

    const { Icon } = getVariantMeta(toast.variant);

    return (
        <Slide
            direction="down"
            in={visible}
            mountOnEnter
            unmountOnExit
            onExited={handleExited}
        >
            <Paper
                elevation={6}
                sx={{
                    px: 1.5,
                    py: 1.5,
                    borderRadius: 5,
                    bgcolor: "#121212",
                    color: "white",
                    minWidth: { xs: "90vw", sm: 380 },
                    maxWidth: 520,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AppIcon
                        src={Icon}
                        sx={{ width: 44, height: 44 }}
                        ariaLabel={`${toast.variant} notification`}
                    />

                    <Box sx={{ flex: 1, minWidth: 0, gap: 0.5, display: "flex", flexDirection: "column" }}>
                        {toast.title && (
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, lineHeight: 1.1 }}
                                noWrap
                            >
                                {toast.title}
                            </Typography>
                        )}
                        {!!toast.message && (
                            <Typography
                                variant="body2"
                                sx={{ opacity: 0.75, lineHeight: 1.25 }}
                            >
                                {toast.message}
                            </Typography>
                        )}
                    </Box>

                    <IconButton
                        size="small"
                        onClick={startDismiss}
                        sx={{ color: "rgba(255,255,255,0.85)" }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Paper>
        </Slide>
    );
}

export default function GlobalToast() {
    const { active } = useSelector((s) => s.toast);

    if (!active.length) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                top: 30,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1400,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                pointerEvents: "none",
                "& > *": { pointerEvents: "auto" },
            }}
        >
            {active.map((toast) => (
                <SingleToast key={toast.id} toast={toast} />
            ))}
        </Box>
    );
}
